import React, { useState } from 'react';
import { Save, Eye, EyeOff, Lock } from 'lucide-react';
import { useFlashMessage } from '../components/FlashMessage';
import { useAuth } from '../context/AuthContext';
import { authAPI, handleApiError } from '../services/api';

const Settings = () => {
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();
  const { logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const validatePassword = () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showDanger('Yeni şifreler eşleşmiyor.');
      return false;
    }

    if (passwordData.newPassword.length < 6) {
      showDanger('Yeni şifre en az 6 karakter olmalıdır.');
      return false;
    }

    return true;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!validatePassword()) return;

    setIsLoading(true);

    try {
      const requestData = {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      };

      const response = await authAPI.changePassword(requestData);

      if (response.data.success) {
        showSuccess('Şifre başarıyla değiştirildi. Yeniden giriş yapmanız gerekiyor...');

        // Wait 2 seconds to show success message, then logout
        setTimeout(() => {
          logout();
        }, 2000);
      } else {
        showDanger(response.data.message || 'Şifre değiştirme başarısız');
        setIsLoading(false);
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      showDanger(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <FlashContainer />
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Şifre Değiştir</h2>
            <p className="text-sm text-gray-500">Hesap güvenliğiniz için şifrenizi güncelleyin</p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="p-6 space-y-6">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Mevcut Şifre
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  id="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  className="form-input pr-10"
                  placeholder="Mevcut şifrenizi girin"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#7a2c2c] hover:text-[#5a1f1f]"
                >
                  {showCurrentPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Yeni Şifre
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  className="form-input pr-10"
                  placeholder="Yeni şifrenizi girin"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#7a2c2c] hover:text-[#5a1f1f]"
                >
                  {showNewPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Yeni Şifre (Tekrar)
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="form-input pr-10"
                  placeholder="Yeni şifrenizi tekrar girin"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#7a2c2c] hover:text-[#5a1f1f]"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex items-start space-x-3">
                <Lock className="h-5 w-5 text-[#7a2c2c] mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Şifre Güvenlik Kuralları</h3>
                  <ul className="text-sm text-gray-700 mt-1 space-y-1">
                    <li>• En az 6 karakter uzunluğunda olmalıdır</li>
                    <li>• Büyük ve küçük harf içermelidir</li>
                    <li>• En az bir rakam içermelidir</li>
                    <li>• Özel karakter kullanması önerilir</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                className="btn-primary flex items-center space-x-2"
              >
                <Save className="h-5 w-5" />
                <span>{isLoading ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}</span>
              </button>
            </div>
          </form>
        </div>
    </div>
  );
};

export default Settings;